"use client";

import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import {
  cardClick,
  ctaClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  navigationClick,
} from "@/customAnalytics/trackingEvents";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { extractYouTubeVideoId } from "@/utils/video";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { shadowFromColor } from "@/utils/utils";

const ArrowLeftIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15 19.92L8.48 13.4C7.71 12.63 7.71 11.37 8.48 10.6L15 4.08"
      stroke="white"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.91 19.92L15.43 13.4C16.2 12.63 16.2 11.37 15.43 10.6L8.91 4.08"
      stroke="white"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface Testimonials2Item {
  id: string;
  quoteIcon?: string;
  quoteIconAlt?: string;
  name?: string;
  nameColor?: string;
  designation?: string;
  designationColor?: string;
  description?: string;
  descriptionColor?: string;
  placedAtText?: string;
  placedAtColor?: string;
  logo?: string;
  logoAlt?: string;
  studentImage?: string;
  studentImageAlt?: string;
  studentMobileImage?: string;
  studentMobileImageAlt?: string;
  videoLink?: string;
}

/**
 * Per-breakpoint pair widths (quote + image − 40px overlap):
 *   sm (1080): 240 + 232 − 40 = 432
 *   md (1230): 280 + 260 − 40 = 500
 *   lg (1430): 300 + 280 − 40 = 540
 */
const PAIR_WIDTH = { sm: 432, md: 500, lg: 540 };

/**
 * Mirrors section horizontal `pl-*` (see `<section>`) so static rows use matching `pr-*`.
 * Section uses `pr-0`, which skews `justify-center` toward the right; pairing fixes true centering.
 */
const STATIC_ROW_SECTION_X_PAD = [
  "pr-5",
  "sm:pr-[60px]",
  "md:pr-[80px]",
  "lg:pr-[100px]",
  "xl:pr-[160px]",
] as const;

/**
 * Milliseconds from CMS — same pattern as ProgramBenefits2, Placements3, Faculty2.
 * Empty / invalid / ≤ 0 disables auto-scroll.
 */
function parseScrollFrequencyMs(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === "") return null;
  const n =
    typeof raw === "string" ? parseInt(String(raw).trim(), 10) : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** CMS may send "90deg", "90", or "to right" — linear-gradient() needs a valid angle/side. */
function normalizeTitleGradientAngle(raw: unknown): string {
  const t = String(raw ?? "").trim();
  if (!t) return "90deg";
  if (/^to\s/i.test(t)) return t;
  if (/deg|grad|rad|turn$/i.test(t)) return t;
  if (/^-?\d+(\.\d+)?$/.test(t)) return `${t}deg`;
  return t;
}

/** Section header + card copy caps (truncate when exceeded). */
const TESTIMONIALS2_COPY_LIMITS = {
  preTitle: 20,
  title: 20,
  cardDescription: 130,
  cardName: 25,
  cardDesignation: 25,
} as const;

function clipTestimonials2Text(value: unknown, maxLen: number): string {
  const s = String(value ?? "");
  return s.length <= maxLen ? s : s.slice(0, maxLen);
}

function isYouTubeVideoUrl(url?: string) {
  return Boolean(url?.trim() && extractYouTubeVideoId(url.trim()));
}

/**
 * Same blur stack as ProgramBenefits2 `BenefitCard` when `meta_card_blur_effect` is on
 * (backdrop-blur / saturate / brightness / mask — see ProgramBenefits2.tsx).
 */
const TESTIMONIAL_IMAGE_BLUR_STACK = [
  "backdrop-blur-[5px]",
  "backdrop-saturate-[70%]",
  "backdrop-brightness-50",
  "[mask-image:linear-gradient(to_top,black_0%,black_65%,rgba(0,0,0,0.2)_90%,transparent_97%)]",
] as const;


/** Frosted quote panel — identical on stacked mobile card and desktop quote column. */
const TESTIMONIAL_QUOTE_FROST_STACK = [
  "bg-[rgba(255,255,255,0.14)]",
  "backdrop-blur-[5px]",
  "backdrop-saturate-[70%]",
] as const;

// Figma 518:3466 — light maroon-tint gradient behind the quote/description text.
const TESTIMONIAL_QUOTE_GRADIENT_V2 =
  "linear-gradient(180deg, rgba(131,1,20,0.1) 0%, rgba(29,0,4,0.1) 100%)";

function TestimonialMediaBottomBar({
  item,
  density,
  isV2 = false,
}: {
  item: Testimonials2Item;
  density: "compact" | "comfortable";
  isV2?: boolean;
}) {
  if (!item.placedAtText && !item.logo) return null;

  const barHeight =
    density === "compact"
      ? "h-[59px]"
      : "h-[66px] sm:h-[152px] lg:h-[163px]";
  const bottomOffset =
    density === "compact"
      ? "bottom-[8px]"
      : "bottom-[10px] sm:bottom-[14px] lg:bottom-[16px]";
  const labelClass =
    density === "compact"
      ? "whitespace-nowrap text-center text-[8px] font-medium italic leading-[1.4] tracking-[0.08px]"
      : "whitespace-nowrap text-center text-[12px] font-medium italic leading-[1.4] tracking-[0.12px]";
  const logoWrap =
    density === "compact"
      ? "relative h-[20px] w-[70px]"
      : "relative h-[34px] w-[120px] md:h-[34px] md:w-[120px]";

  return (
    <>
      {/* Image bottom fade overlay — v1 only. For v2 the image stays clean
          (no gradient section covering it). */}
      {!isV2 && (
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 z-5",
            barHeight,
            TESTIMONIAL_IMAGE_BLUR_STACK,
          )}
        />
      )}
      <div
        className={cn(
          "absolute left-1/2 z-6 flex -translate-x-1/2 flex-col items-center gap-[3px]",
          bottomOffset
        )}
      >
        {item.placedAtText ? (
          <p
            className={labelClass}
            style={{
              color: item.placedAtColor || "rgba(255,255,255,0.8)",
              fontFamily: "var(--font-google-sans)",
            }}
          >
            {item.placedAtText}
          </p>
        ) : null}
        {item.logo ? (
          <div className={logoWrap}>
            <Image
              src={item.logo}
              alt={item.logoAlt || ""}
              fill
              className="object-contain"
              sizes={density === "compact" ? "70px" : "(max-width: 1229px) 78px, 90px"}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}

/** Centered play control — only when a valid YouTube URL is configured */
function TestimonialVideoPlayButton({
  onPlay,
  className,
}: {
  onPlay: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 z-20 flex items-center justify-center",
        className
      )}
    >
      <button
        type="button"
        data-cta-name="testimonial_card"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onPlay();
        }}
        className={cn(
          "pointer-events-auto flex size-12 cursor-pointer items-center justify-center rounded-full",
          "bg-white/92 text-[#0a0a0a] shadow-lg ring-1 ring-black/10 transition hover:scale-105 hover:bg-white",
          "sm:size-14 md:size-16"
        )}
        aria-label="Play testimonial video"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="ml-0.5"
          aria-hidden
        >
          <path d="M8 5v14l11-7z" />
        </svg>
      </button>
    </div>
  );
}

function TestimonialPair({
  item,
  onQuoteClick,
  onMediaClick,
  isV2 = false
}: {
  item: Testimonials2Item;
  onQuoteClick: () => void;
  onMediaClick: () => void;
  isV2?: boolean,
}) {
  const showVideoPlay = isYouTubeVideoUrl(item.videoLink);

  return (
    <div className="flex flex-col items-stretch sm:flex-row sm:items-start">
      {/* Mobile stacked card — image top + quote bottom */}
      <div className="flex h-[400px] w-[280px] flex-col sm:hidden">
        {/* Image as direct child (not inside <button>) so backdrop-filter on the bar blurs the photo on WebKit/mobile. */}
        <div className="relative isolate h-[190px] w-full shrink-0 overflow-hidden rounded-tl-[20px] rounded-tr-[20px]">
          {item.studentMobileImage ? (
            <>
              <Image
                src={item.studentMobileImage}
                alt={item.studentMobileImageAlt || ""}
                fill
                className="pointer-events-none object-cover select-none"
                sizes="280px"
                draggable={false}
              />
              <button
                type="button"
                className="absolute inset-0 z-1 cursor-pointer border-0 bg-transparent p-0"
                onClick={onMediaClick}
                data-cta-name="testimonial_card"
                aria-label={item.studentMobileImageAlt || "Student"}
              />
            </>
          ) : (
            <div className="h-full w-full bg-[#1a1a1a]" />
          )}
          <TestimonialMediaBottomBar item={item} isV2={isV2} density="compact" />
          {showVideoPlay ? (
            <TestimonialVideoPlayButton onPlay={onMediaClick} />
          ) : null}
        </div>

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col items-start gap-[14px] rounded-bl-[20px] rounded-br-[20px] p-[14px]",
            isV2 ? "" : TESTIMONIAL_QUOTE_FROST_STACK,
          )}
          style={isV2 ? { background: TESTIMONIAL_QUOTE_GRADIENT_V2 } : undefined}
          onClick={onQuoteClick}
          data-cta
          data-cta-name="testimonial_card"
          role="presentation"
        >
          {item.quoteIcon ? (
            <div className="relative size-[26px] shrink-0">
              <Image
                src={item.quoteIcon}
                alt={item.quoteIconAlt || "Quote"}
                fill
                className="object-cover"
                sizes="26px"
              />
            </div>
          ) : null}

          <p
            className="min-h-0 flex-1 text-sm font-semibold leading-[1.49]"
            style={{
              color: isV2 ? "rgba(0,0,0,0.8)" : item.descriptionColor || "rgba(255,255,255,0.8)",
              fontFamily: "var(--font-google-sans)",
            }}
          >
            {item.description}
          </p>

          <div className="mt-auto flex w-full shrink-0 flex-col items-start leading-[1.4]">
            {item.name ? (
              <p
                className="w-full font-denton-test text-[15.6px] font-bold tracking-[0.16px]"
                style={{ color: isV2 ? "#101112" : item.nameColor || "#FFFFFF" }}
              >
                {item.name}
              </p>
            ) : null}
            {item.designation ? (
              <p
                className="font-denton-test text-[13px] font-medium italic tracking-[0.13px]"
                style={{ color: isV2 ? "#101112" : item.designationColor || "#FFFFFF" }}
              >
                {item.designation}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Desktop side-by-side layout */}
      {/* Quote card — sm: 240, md: 280 */}
      <div
        className={cn(
          "relative z-1 hidden w-full flex-col items-start rounded-[20px] sm:flex",
          isV2 ? "" : TESTIMONIAL_QUOTE_FROST_STACK,
          "sm:mr-[-40px] sm:w-[240px] sm:min-w-[240px] sm:max-w-[240px] sm:self-stretch sm:gap-[20px] sm:pl-[20px] sm:pr-[50px] sm:py-[20px]",
          "md:w-[280px] md:min-w-[280px] md:max-w-[280px] md:h-[380px] md:self-auto md:pl-[30px] md:pr-[50px] md:py-[20px]",
          "lg:w-[300px] lg:min-w-[300px] lg:max-w-[300px] lg:h-[408px]"
        )}
        style={isV2 ? { background: TESTIMONIAL_QUOTE_GRADIENT_V2 } : undefined}
        onClick={onQuoteClick}
        data-cta
        data-cta-name="testimonial_card"
        role="presentation"
      >
        {item.quoteIcon ? (
          <div className="relative size-[30px] shrink-0 lg:size-[40px]">
            <Image
              src={item.quoteIcon}
              alt={item.quoteIconAlt || "Quote"}
              fill
              className="object-cover"
              sizes="(min-width: 1430px) 40px, 30px"
            />
          </div>
        ) : null}

        <p
          className={cn(
            "min-h-0 flex-1",
            // Figma 518:3468 v2 desktop: Poppins Medium 17px.
            isV2
              ? "font-medium leading-[1.65] text-[17px]"
              : cn(
                  "font-semibold leading-[1.49]",
                  "sm:font-wf-visual-sans sm:text-[14px]",
                  "md:text-[16px]",
                  "lg:text-[18px]"
                ),
          )}
          style={{
            color: isV2 ? "rgba(0,0,0,0.8)" : item.descriptionColor || "rgba(255,255,255,0.8)",
            fontFamily: isV2 ? "var(--font-poppins)" : undefined,
          }}
        >
          {item.description}
        </p>

        <div className="mt-auto flex w-full shrink-0 flex-col items-start leading-140">
          {item.name ? (
            <p
              className={cn(
                "w-full font-denton-test font-bold -tracking-018",
                "sm:text-[18px] sm:-tracking-018",
                "md:text-[20px] md:tracking-[0.2px]",
                "lg:text-[24px] lg:tracking-[0.24px]"
              )}
              style={{ color: isV2 ? "#101112" : item.nameColor || "#FFFFFF" }}
            >
              {item.name}
            </p>
          ) : null}
          {item.designation ? (
            <p
              className={cn(
                "font-denton-test font-medium italic -tracking-012",
                "sm:text-[12px] sm:-tracking-012",
                "md:text-[14px] md:-tracking-014",
                "lg:text-[20px] lg:tracking-[0.2px]"
              )}
              style={{ color: isV2 ? "#101112" : item.designationColor || "#FFFFFF" }}
            >
              {item.designation}
            </p>
          ) : null}
        </div>
      </div>

      {/* Media card — sm: 232×340, md: 260×380 */}
      <div
        className={cn(
          "relative isolate z-2 hidden w-full overflow-hidden rounded-[20px] border-[0.5px] border-[rgba(255,255,255,0.4)] sm:block",
          "sm:h-[340px] sm:w-[232px] sm:min-w-[232px] sm:max-w-[232px] sm:rounded-[25px]",
          "md:h-[380px] md:w-[260px] md:min-w-[260px] md:max-w-[260px] md:rounded-[28px] md:border-[0.559px]",
          "lg:h-[408px] lg:w-[280px] lg:min-w-[280px] lg:max-w-[280px] lg:rounded-[30px] lg:border-[0.6px]"
        )}
      >
        {item.studentImage ? (
          <>
            <Image
              src={item.studentImage}
              alt={item.studentImageAlt || ""}
              fill
              className="pointer-events-none object-cover select-none"
              draggable={false}
              sizes="(max-width: 1079px) 100vw, (max-width: 1229px) 232px, (max-width: 1429px) 260px, 280px"
            />
            <button
              type="button"
              className="absolute inset-0 z-1 cursor-pointer border-0 bg-transparent p-0"
              onClick={onMediaClick}
              data-cta-name="testimonial_card"
              aria-label={item.studentImageAlt || "Student"}
            />
          </>
        ) : (
          <div className="h-full w-full bg-[#1a1a1a]" />
        )}
        <TestimonialMediaBottomBar isV2={isV2} item={item} density="comfortable" />
        {showVideoPlay ? (
          <TestimonialVideoPlayButton onPlay={onMediaClick} />
        ) : null}
      </div>
    </div>
  );
}

const Testimonials2 = ({ data }: { data: any }) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(1080);
  /** Tailwind `sm` (640px): static 2-card row from this width up on the <1080 mobile carousel branch. */
  const isBelowSm = useIsMobile(640);
  const isBelowMd = useIsMobile(1230);
  const isBelowLg = useIsMobile(1430);
  const currentPairWidth =
    isBelowLg === false
      ? PAIR_WIDTH.lg
      : isBelowMd === false
        ? PAIR_WIDTH.md
        : PAIR_WIDTH.sm;

  const sectionData = data?.data || {};

  const {
    desktop_background_color,
    mobile_background_color,
    backvariant = "variant_1",
    gradient_color_1,
    gradient_color_2,
    gradient_color_3,
    gradient_color_4,
    pre_title = "",
    pre_title_color = "rgba(255,255,255,0.8)",
    title = "",
    title_start_color = "#FFFFFF",
    title_end_color = "#02A7B6",
    title_gradient_angle = "90deg",
    left_navigation_icon,
    left_navigation_icon_alt = "Previous",
    right_navigation_icon,
    right_navigation_icon_alt = "Next",
    button_text,
    button_text_color = "#222222",
    button_color = "#FFFFFF",
    button_icon = "",
    enable_animation = false,
    scroll_frequency,
    metadata,
    layout_variant = "variant_1",
  } = sectionData;

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  const displayPreTitle = clipTestimonials2Text(
    pre_title,
    TESTIMONIALS2_COPY_LIMITS.preTitle
  );
  
  const autoplayIntervalMs = useMemo(
    () => parseScrollFrequencyMs(scroll_frequency),
    [scroll_frequency]
  );
  
  const isV2 = layout_variant === "variant_2";

  const displayTitle = clipTestimonials2Text(title, isV2 ? 100 : TESTIMONIALS2_COPY_LIMITS.title);

  const testimonials: Testimonials2Item[] = useMemo(
    () =>
      metadata
        ?.filter((item: any) => item.meta_status)
        ?.sort(
          (a: any, b: any) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
        )
        ?.map((item: any) => ({
          id: String(item.id),
          quoteIcon: item.meta_quote_icon,
          quoteIconAlt: item.meta_alt_icon_image,
          name: clipTestimonials2Text(item.meta_name, TESTIMONIALS2_COPY_LIMITS.cardName),
          nameColor: item.meta_name_text_color,
          designation: clipTestimonials2Text(
            item.meta_designation,
            TESTIMONIALS2_COPY_LIMITS.cardDesignation
          ),
          designationColor: item.meta_designation_text_color,
          description: clipTestimonials2Text(
            item.meta_description,
            TESTIMONIALS2_COPY_LIMITS.cardDescription
          ),
          descriptionColor: item.meta_description_text_color,
          placedAtText: item.meta_placed_at_text,
          placedAtColor: item.meta_placed_at_text_color,
          logo: item.meta_logo,
          logoAlt: item.meta_alt_icon,
          studentImage: item.meta_student_image,
          studentImageAlt: item.meta_student_image_alt,
          studentMobileImage: item.meta_student_mobile_image || item.meta_student_image,
          studentMobileImageAlt: item.meta_student_mobile_image_alt || item.meta_student_image_alt,
          videoLink: item.meta_video_link,
        })) || [],
    [metadata]
  );

  /** ≥sm (640px), two cards: centered row, no carousel (mobile branch is <1080 only). */
  const mobileStaticTwo =
    isMobile === true &&
    testimonials.length === 2 &&
    isBelowSm === false;
  /** <sm, one card: single column, no scroll / no autoplay. */
  const mobileStaticOne =
    isMobile === true &&
    testimonials.length === 1 &&
    isBelowSm === true;
  /** Desktop (≥1080): two cards — centered row, no carousel (always ≥sm). */
  const desktopTwoStatic =
    isMobile === false && testimonials.length === 2;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: testimonials.length > 2,
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const [mobileEmblaRef, mobileEmblaApi] = useEmblaCarousel({
    align: "start",
    loop: testimonials.length > 2,
    slidesToScroll: 1,
    containScroll: "keepSnaps",
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [mobileSelectedIndex, setMobileSelectedIndex] = useState(0);
  const [mobileSnapCount, setMobileSnapCount] = useState(0);

  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState("");

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setSnapCount(emblaApi.scrollSnapList().length);
  }, [emblaApi]);

  const onMobileSelect = useCallback(() => {
    if (!mobileEmblaApi) return;
    setMobileSelectedIndex(mobileEmblaApi.selectedScrollSnap());
    setMobileSnapCount(mobileEmblaApi.scrollSnapList().length);
  }, [mobileEmblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
  }, [emblaApi, testimonials.length]);

  useEffect(() => {
    if (!mobileEmblaApi) return;
    onMobileSelect();
    mobileEmblaApi.on("select", onMobileSelect).on("reInit", onMobileSelect);
  }, [mobileEmblaApi, onMobileSelect]);

  useEffect(() => {
    if (!mobileEmblaApi) return;
    mobileEmblaApi.reInit();
  }, [mobileEmblaApi, testimonials.length]);

  /** Auto-advance carousel when scroll_frequency is set (paused while video modal is open). */
  useEffect(() => {
    if (isMobile === null) return;
    if (videoOpen) return;
    if (autoplayIntervalMs == null || testimonials.length <= 1) return;
    if (testimonials.length === 2 && isBelowSm !== true) return;

    const api = isMobile ? mobileEmblaApi : emblaApi;
    if (!api) return;

    const id = window.setInterval(() => {
      api.scrollNext();
    }, autoplayIntervalMs);

    return () => window.clearInterval(id);
  }, [
    autoplayIntervalMs,
    testimonials.length,
    videoOpen,
    isMobile,
    emblaApi,
    mobileEmblaApi,
    isBelowSm,
  ]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    window.setTimeout(() => {
      navigationClick({
        section: "student_testimonials_2_section",
        component: "carousel_navigation",
        label: "previous",
        index: String(emblaApi.selectedScrollSnap()),
      });
    }, 0);
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    window.setTimeout(() => {
      navigationClick({
        section: "student_testimonials_2_section",
        component: "carousel_navigation",
        label: "next",
        index: String(emblaApi.selectedScrollSnap()),
      });
    }, 0);
  }, [emblaApi]);

  const scrollMobilePrev = useCallback(() => {
    mobileEmblaApi?.scrollPrev();
  }, [mobileEmblaApi]);

  const scrollMobileNext = useCallback(() => {
    mobileEmblaApi?.scrollNext();
  }, [mobileEmblaApi]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "student_testimonials_2_section");
  //   }
  // }, []);

  const progressWidth =
    snapCount > 0 ? ((selectedIndex + 1) / snapCount) * 100 : 0;
  const mobileProgressWidth =
    mobileSnapCount > 0
      ? ((mobileSelectedIndex + 1) / mobileSnapCount) * 100
      : 0;

  const bgStyle = useMemo(() => {
    const base =
      (isMobile ? mobile_background_color : desktop_background_color) ||
      "#000000";
    return formatBackgroundStyle(
      {
        backvariant: backvariant as "variant_1" | "variant_2" | "variant_3",
        gradient_color_1: gradient_color_1 || "#02A7B6",
        gradient_color_2: gradient_color_2 || "#000000",
        gradient_color_3: gradient_color_3 || "#000000",
        gradient_color_4: gradient_color_4 || "#5345C3",
        desktop_background_color,
        mobile_background_color,
      },
      !!isMobile,
      base
    );
  }, [
    backvariant,
    desktop_background_color,
    gradient_color_1,
    gradient_color_2,
    gradient_color_3,
    gradient_color_4,
    isMobile,
    mobile_background_color,
  ]);

  if (!data?.data || !data?.data?.status) {
    return null;
  }

  if (!title || !testimonials.length) {
    return null;
  }

  const handleCardClick = (item: Testimonials2Item, index: number) => {
    cardClick({
      section: "student_testimonials_2_section",
      component: "testimonial_card",
      label: item.name || `Testimonial ${index + 1}`,
      parent: title,
      redirectLink: "",
      pageKey: "",
    });
  };

  const openVideo = (url: string) => {
    setActiveVideoUrl(url);
    setVideoOpen(true);
  };

  const titleGradientAngle = normalizeTitleGradientAngle(title_gradient_angle);

  const renderPair = (
    item: Testimonials2Item,
    index: number,
    layout: "mobile" | "desktop"
  ) => (
    <TestimonialPair
      key={`${layout}-${item.id}`}
      isV2={isV2}
      item={item}
      onQuoteClick={() => handleCardClick(item, index)}
      onMediaClick={() => {
        handleCardClick(item, index);
        const url = item.videoLink?.trim();
        if (url && extractYouTubeVideoId(url)) openVideo(url);
      }}
    />
  );

  return (
    <>
    <section
      ref={ref}
      className={cn(
        "relative flex w-full flex-col items-center overflow-hidden",
        "gap-6 py-10 pl-5 pr-0",
        "sm:gap-[40px] sm:pl-[60px] sm:pr-0 sm:py-[40px]",
        "md:gap-[60px] md:pl-[80px] md:pr-0 md:py-[60px]",
        "lg:pl-[100px] lg:pr-0",
        "xl:py-[80px] xl:pl-[160px] xl:pr-0"
      )}
      style={bgStyle}
    >
      <div className="flex w-full flex-col gap-1 sm:gap-[2px]">
        {displayPreTitle ? (
          <RevealOnScroll enableAnimation={isAnimationEnabled}>
          <p
            className={cn(
              "w-full font-semibold leading-normal",
              isV2 ? "font-poppins text-center" : "font-wf-visual-sans",
              "text-2xl",
              "sm:text-[22px]",
              "md:text-[26px]",
              "lg:text-[30px]",
            )}
            style={{ color: pre_title_color }}
          >
            {displayPreTitle}
          </p>
          </RevealOnScroll>
        ) : null}

        <RevealOnScroll enableAnimation={isAnimationEnabled}>
        <p
          className={cn(
            "w-full bg-clip-text text-transparent",
            isV2
              ? // Figma 518:3463: Poppins SemiBold 28px (mobile) → 36px (web); tracking -0.42 → -0.54px.
                "text-center font-poppins font-semibold leading-[1.25] text-[28px] tracking-[-0.42px] sm:text-[36px] sm:tracking-[-0.54px]"
              : cn(
                  "font-denton-test font-bold leading-[1.2]",
                  "text-[40px] tracking-[1.2px]",
                  "sm:text-[46px] sm:tracking-normal",
                  "md:text-[56px]",
                  "lg:text-[64px]"
                ),
          )}
          style={{
            backgroundImage: `linear-gradient(${titleGradientAngle}, ${title_start_color || "#FFFFFF"}, ${title_end_color || "#02A7B6"})`,
            fontFamily: isV2 ? "var(--font-poppins)" : "var(--font-merriweather)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
          }}
        >
          {displayTitle}
        </p>
        </RevealOnScroll>
      </div>

      {isMobile ? (
        <>
          {mobileStaticTwo ? (
            <RevealOnScroll y={0} x={-200} enableAnimation={isAnimationEnabled}>
            <div
              className={cn(
                "w-full self-stretch overflow-visible",
                STATIC_ROW_SECTION_X_PAD
              )}
            >
              <div className="flex w-full flex-wrap justify-center gap-6">
                {testimonials.map((item, index) => (
                  <div key={item.id} className="w-[280px] shrink-0">
                    {renderPair(item, index, "mobile")}
                  </div>
                ))}
              </div>
            </div>
            </RevealOnScroll>
          ) : mobileStaticOne ? (
            <RevealOnScroll y={0} x={-200} enableAnimation={isAnimationEnabled}>
            <div
              className={cn(
                "flex w-full self-stretch justify-center overflow-visible",
                STATIC_ROW_SECTION_X_PAD
              )}
            >
              <div className="w-[280px] shrink-0">
                {renderPair(testimonials[0], 0, "mobile")}
              </div>
            </div>
            </RevealOnScroll>
          ) : (
            <RevealOnScroll y={0} x={-200} enableAnimation={isAnimationEnabled} className="w-full self-stretch overflow-hidden">
            <div ref={mobileEmblaRef} className={cn(
              "w-full self-stretch overflow-hidden",
              ...STATIC_ROW_SECTION_X_PAD
            )}>
              {/* Stride = card + gutter in the slide box so Embla loop clones keep the same gap as gap-6 (1.5rem). */}
              <div className="flex">
                {testimonials.map((item, index) => (
                  <div
                    key={item.id}
                    className="box-border flex min-w-0 w-[calc(280px+1.5rem)] shrink-0 flex-[0_0_calc(280px+1.5rem)]"
                  >
                    <div className="w-[280px] shrink-0">
                      {renderPair(item, index, "mobile")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </RevealOnScroll>
          )}
          {testimonials.length > 1 && !mobileStaticTwo ? (
            <div className="flex w-full items-center gap-5 pr-5">
              <button
                type="button"
                data-cta-name="carousel_nav"
                onClick={scrollMobilePrev}
                className={cn(
                  "flex size-11 shrink-0 cursor-pointer items-center justify-center transition-opacity",
                  left_navigation_icon
                    ? "overflow-hidden"
                    : "rounded-[80px] border border-white p-2.5 hover:bg-white/10"
                )}
                aria-label={left_navigation_icon_alt || "Previous"}
              >
                {left_navigation_icon ? (
                  <Image
                    src={left_navigation_icon}
                    alt={left_navigation_icon_alt || "Previous"}
                    width={44}
                    height={44}
                    className="size-full object-contain"
                  />
                ) : (
                  <div className="flex size-6 items-center justify-center">
                    <ArrowLeftIcon />
                  </div>
                )}
              </button>
              <div
                className="relative h-[6px] min-w-0 flex-1 overflow-hidden rounded-[10px]"
                style={{
                  backgroundColor: isV2
                    ? "rgba(16, 17, 18, 0.15)"
                    : "rgba(255, 255, 255, 0.2)",
                }}
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-[10px] transition-all duration-300 ease-out"
                  style={{
                    width: `${mobileProgressWidth}%`,
                    backgroundColor: isV2 ? "#101112" : "#FFFFFF",
                  }}
                />
              </div>
              <button
                type="button"
                data-cta-name="carousel_nav"
                onClick={scrollMobileNext}
                className={cn(
                  "flex size-11 shrink-0 cursor-pointer items-center justify-center transition-opacity",
                  right_navigation_icon
                    ? "overflow-hidden"
                    : "rounded-[80px] border border-white p-2.5 hover:bg-white/10"
                )}
                aria-label={right_navigation_icon_alt || "Next"}
              >
                {right_navigation_icon ? (
                  <Image
                    src={right_navigation_icon}
                    alt={right_navigation_icon_alt || "Next"}
                    width={44}
                    height={44}
                    className="size-full object-contain"
                  />
                ) : (
                  <div className="flex size-6 items-center justify-center">
                    <ArrowRightIcon />
                  </div>
                )}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {desktopTwoStatic ? (
            <RevealOnScroll y={0} x={-200} enableAnimation={isAnimationEnabled}>
            <div
              className={cn("w-full overflow-visible", STATIC_ROW_SECTION_X_PAD)}
            >
              <div className="flex w-full flex-wrap justify-center gap-[40px]">
                {testimonials.map((item, index) => (
                  <div
                    key={item.id}
                    className="isolate min-w-0 shrink-0 overflow-hidden"
                    style={{
                      flex: `0 0 ${currentPairWidth}px`,
                      width: currentPairWidth,
                      maxWidth: currentPairWidth,
                    }}
                  >
                    {renderPair(item, index, "desktop")}
                  </div>
                ))}
              </div>
            </div>
            </RevealOnScroll>
          ) : (
            <RevealOnScroll y={0} x={-200} enableAnimation={isAnimationEnabled} className="w-full overflow-hidden">
            <div ref={emblaRef} className={cn(
              "w-full overflow-hidden",
              ...STATIC_ROW_SECTION_X_PAD
            )} >
              {/* Stride = card + 40px gutter baked into the slide box so Embla loop clones
                  keep the same gap as the static gap-[40px] rows (no collapsed seam). */}
              <div className="flex">
                {testimonials.map((item, index) => (
                  <div
                    key={item.id}
                    className="box-border flex min-w-0 shrink-0"
                    style={{
                      flex: `0 0 ${currentPairWidth + 40}px`,
                      width: currentPairWidth + 40,
                      maxWidth: currentPairWidth + 40,
                    }}
                  >
                    <div
                      className="isolate min-w-0 shrink-0 overflow-hidden"
                      style={{
                        flex: `0 0 ${currentPairWidth}px`,
                        width: currentPairWidth,
                        maxWidth: currentPairWidth,
                      }}
                    >
                      {renderPair(item, index, "desktop")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </RevealOnScroll>
          )}

          {testimonials.length > 2 ? (
            <div className="flex w-full items-center justify-center gap-[16px] md:w-[418px] md:gap-[20px]">
              <button
                type="button"
                data-cta-name="carousel_nav"
                onClick={scrollPrev}
                className={cn(
                  "flex shrink-0 cursor-pointer items-center justify-center transition-opacity",
                  left_navigation_icon
                    ? "size-[40px] overflow-hidden md:size-[44px]"
                    : cn(
                        "rounded-[80px] border border-white bg-[rgba(255,255,255,0.2)] hover:bg-white/30",
                        "size-[40px] p-[8.5px]",
                        "md:size-auto md:p-[10px]"
                      )
                )}
                aria-label={left_navigation_icon_alt || "Previous"}
              >
                {left_navigation_icon ? (
                  <Image
                    src={left_navigation_icon}
                    alt={left_navigation_icon_alt || "Previous"}
                    width={44}
                    height={44}
                    className="size-full object-contain"
                  />
                ) : (
                  <div className="flex size-[20.5px] items-center justify-center md:size-[24px]">
                    <ArrowLeftIcon />
                  </div>
                )}
              </button>

              <div
                className="relative h-[5px] w-[248px] shrink-0 overflow-hidden rounded-[8.5px] md:h-[6px] md:w-[290px] md:rounded-[10px]"
                style={{
                  backgroundColor: isV2
                    ? "rgba(16, 17, 18, 0.15)"
                    : "rgba(255, 255, 255, 0.2)",
                }}
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-[8.5px] transition-all duration-300 ease-out md:rounded-[10px]"
                  style={{
                    width: `${progressWidth}%`,
                    backgroundColor: isV2 ? "#101112" : "#FFFFFF",
                  }}
                />
              </div>

              <button
                type="button"
                data-cta-name="carousel_nav"
                onClick={scrollNext}
                className={cn(
                  "flex shrink-0 cursor-pointer items-center justify-center transition-opacity",
                  right_navigation_icon
                    ? "size-[40px] overflow-hidden md:size-[44px]"
                    : cn(
                        "rounded-[80px] border border-white bg-[rgba(255,255,255,0.2)] hover:bg-white/30",
                        "size-[40px] p-[8.5px]",
                        "md:size-auto md:p-[10px]"
                      )
                )}
                aria-label={right_navigation_icon_alt || "Next"}
              >
                {right_navigation_icon ? (
                  <Image
                    src={right_navigation_icon}
                    alt={right_navigation_icon_alt || "Next"}
                    width={44}
                    height={44}
                    className="size-full object-contain"
                  />
                ) : (
                  <div className="flex size-[20.5px] items-center justify-center md:size-[24px]">
                    <ArrowRightIcon />
                  </div>
                )}
              </button>
            </div>
          ) : null}
        </>
      )}

      {button_text ? (
        <RevealOnScroll
          className={isV2 ? "w-full sm:w-auto" : ""}
          enableAnimation={isAnimationEnabled}
        >
        <div className="flex w-full justify-center pr-5 sm:pr-0">
          <button
            type="button"
            data-cta-name="apply_now"
            className={cn(
              "cursor-pointer font-semibold leading-[1.4] transition-opacity hover:opacity-90",
              "w-full px-6 py-4 text-base tracking-[0.16px]",
              "sm:w-auto sm:rounded-lg sm:px-6 sm:py-3 sm:tracking-normal",
              // v2: rounded corners on mobile + flex layout for the export icon.
              isV2 && "flex items-center justify-center gap-[10px] rounded-lg",
            )}
            style={{
              backgroundColor: button_color,
              color: button_text_color,
              fontFamily: "var(--font-google-sans)",
              // v2: soft drop-shadow derived from the button's own colour (same as Certificates/Faculty).
              filter: isV2
                ? `drop-shadow(0px 10px 10px ${shadowFromColor(button_color)})`
                : undefined,
            }}
            onClick={() => {
              ctaClick({
                section: "student_testimonials_2_section",
                component: "apply_button",
                label: button_text || "apply",
                step: "1",
                entity: "student_testimonials_2_section",
                parentEntity: "student_testimonials_2_section",
              });
              dispatch(
                handleOpenLeaddrop({
                  variant: LEAD_DROP_VARIANTS.REGISTER,
                })
              );
            }}
          >
            {button_text}
            {/* Show the CMS button icon only when one is provided — no default icon. */}
            {isV2 && button_icon ? (
              <Image
                src={button_icon}
                alt=""
                width={24}
                height={24}
                className="size-[24px] shrink-0 object-contain"
              />
            ) : null}
          </button>
        </div>
        </RevealOnScroll>
      ) : null}
    </section>

    <YouTubeVideoOverlay
      isOpen={videoOpen}
      onClose={() => {
        setVideoOpen(false);
        setActiveVideoUrl("");
      }}
      videoUrl={activeVideoUrl}
      title="Testimonial video"
    />
    </>
  );
};

export default Testimonials2;
